import React, { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Button, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, FormControl, InputLabel, Select, MenuItem, Divider, Skeleton, CircularProgress, Drawer, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import SupplierModals from './components/SupplierModals';
import PurchaseOrderModals from './components/PurchaseOrderModals';
import InventoryItemModals from './components/InventoryItemModals';
import StockCatalogueTab from './components/StockCatalogueTab';
import PurchaseOrdersTab from './components/PurchaseOrdersTab';
import SuppliersTab from './components/SuppliersTab';
import { SupplierService } from '../../api/services/supplierService';
import { InventoryService } from '../../api/services/inventoryService';
import { StaffService } from '../../api/services/staffService';
import { SettingsService } from '../../api/services/settingsService';
import { toast } from '../../utils/toast';
import { useCurrency, formatCurrency } from '../../utils/currency';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function InventoryManagement() {
    const { currency } = useCurrency();
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isAuditMode = hotelStatus === 'Inactive' || hotelStatus === 'Suspended';

    const [tabValue, setTabValue] = useState(0);

    const [itemsRefreshKey, setItemsRefreshKey] = useState(0);
    const [posRefreshKey, setPosRefreshKey] = useState(0);
    const [suppliersRefreshKey, setSuppliersRefreshKey] = useState(0);

    const [suppliers, setSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);

    // Supplier creation modal states
    const [supplierModalOpen, setSupplierModalOpen] = useState(false);
    const [isSubmittingSupplier, setIsSubmittingSupplier] = useState(false);
    const [supplierForm, setSupplierForm] = useState({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        category: '',
        paymentTerms: ''
    });

    // Supplier edit modal states
    const [editSupplierModalOpen, setEditSupplierModalOpen] = useState(false);
    const [isUpdatingSupplier, setIsUpdatingSupplier] = useState(false);
    const [selectedEditSupplierId, setSelectedEditSupplierId] = useState(null);
    const [editSupplierForm, setEditSupplierForm] = useState({
        contactPerson: '',
        email: ''
    });

    // Supplier delete modal states
    const [deleteSupplierModalOpen, setDeleteSupplierModalOpen] = useState(false);
    const [isDeletingSupplier, setIsDeletingSupplier] = useState(false);
    const [selectedDeleteSupplierId, setSelectedDeleteSupplierId] = useState(null);

    // Supplier detail drawer states
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [supplierDetails, setSupplierDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Inventory Items states
    const [inventoryItems, setInventoryItems] = useState([]);
    const [inventoryItemsLoading, setInventoryItemsLoading] = useState(false);
    const [inventoryItemModalOpen, setInventoryItemModalOpen] = useState(false);
    const [isSubmittingInventoryItem, setIsSubmittingInventoryItem] = useState(false);
    const [inventoryItemForm, setInventoryItemForm] = useState({
        name: '',
        category: '',
        unitOfMeasure: '',
        currentStock: '',
        reorderLevel: '',
        maxStock: '',
        unitCost: '',
        supplierId: ''
    });

    // Inventory Item details states
    const [inventoryItemDrawerOpen, setInventoryItemDrawerOpen] = useState(false);
    const [selectedInventoryItemId, setSelectedInventoryItemId] = useState(null);
    const [inventoryItemDetails, setInventoryItemDetails] = useState(null);
    const [inventoryItemDetailsLoading, setInventoryItemDetailsLoading] = useState(false);
    
    // Inventory Item edit modal states
    const [editInventoryItemModalOpen, setEditInventoryItemModalOpen] = useState(false);
    const [isUpdatingInventoryItem, setIsUpdatingInventoryItem] = useState(false);
    const [selectedEditInventoryItemId, setSelectedEditInventoryItemId] = useState(null);
    const [editInventoryItemForm, setEditInventoryItemForm] = useState({
        name: '',
        unitCost: 0
    });

    // Inventory Item delete modal states
    const [deleteInventoryItemModalOpen, setDeleteInventoryItemModalOpen] = useState(false);
    const [isDeletingInventoryItem, setIsDeletingInventoryItem] = useState(false);
    const [selectedDeleteInventoryItemId, setSelectedDeleteInventoryItemId] = useState(null);

    // Inventory Item adjustment modal states
    const [adjustStockModalOpen, setAdjustStockModalOpen] = useState(false);
    const [isAdjustingStock, setIsAdjustingStock] = useState(false);
    const [selectedAdjustStockItemId, setSelectedAdjustStockItemId] = useState(null);
    const [selectedAdjustStockItem, setSelectedAdjustStockItem] = useState(null);
    const [adjustStockForm, setAdjustStockForm] = useState({
        quantity: '',
        reason: '',
        notes: ''
    });

    // Inventory Item Movements states
    const [itemMovements, setItemMovements] = useState([]);
    const [itemMovementsLoading, setItemMovementsLoading] = useState(false);
    const [movementsPagination, setMovementsPagination] = useState({ page: 1, limit: 10, totalPages: 1 });

    // Purchase Orders states
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false);

    // Create PO states
    const [createPOModalOpen, setCreatePOModalOpen] = useState(false);
    const [isCreatingPO, setIsCreatingPO] = useState(false);
    const [poForm, setPoForm] = useState({
        supplierId: '',
        expectedDeliveryDate: '',
        notes: '',
        items: [{ itemId: '', quantityOrdered: '', unitCost: '' }]
    });

    // PO Details Drawer states
    const [poDrawerOpen, setPoDrawerOpen] = useState(false);
    const [selectedPoId, setSelectedPoId] = useState(null);
    const [poDetails, setPoDetails] = useState(null);
    const [poDetailsLoading, setPoDetailsLoading] = useState(false);
    const [isSubmittingPO, setIsSubmittingPO] = useState(false);
    
    // PO Approval states
    const [isReviewingPO, setIsReviewingPO] = useState(false);
    const [poApproveModalOpen, setPoApproveModalOpen] = useState(false);
    const [poRejectModalOpen, setPoRejectModalOpen] = useState(false);
    const [poRejectReason, setPoRejectReason] = useState('');
    
    // PO Receive states
    const [isReceivingPO, setIsReceivingPO] = useState(false);
    const [poReceiveModalOpen, setPoReceiveModalOpen] = useState(false);
    const [poReceiveForm, setPoReceiveForm] = useState({
        supplierInvoiceNumber: '',
        items: []
    });

    // PO Payment states
    const [poPaymentModalOpen, setPoPaymentModalOpen] = useState(false);
    const [isRecordingPOPayment, setIsRecordingPOPayment] = useState(false);
    const [selectedPOForPayment, setSelectedPOForPayment] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
    const [poPaymentForm, setPoPaymentForm] = useState({
        paymentMethod: 'Bank Transfer',
        amount: '',
        paymentReference: '',
        category: 'Inventory & Supplies',
        paymentDate: '',
        notes: ''
    });

    const fetchPaymentMethods = async () => {
        setLoadingPaymentMethods(true);
        try {
            const res = await SettingsService.getPaymentMethods(tenantId, { noPagination: true });
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
            setPaymentMethods(activeList);
        } catch (err) {
            console.error('Failed to load payment methods from API:', err);
            setPaymentMethods([]);
        } finally {
            setLoadingPaymentMethods(false);
        }
    };

    const fetchSuppliers = async () => {
        setSuppliersLoading(true);
        try {
            const data = await SupplierService.getSuppliers();
            setSuppliers(data || []);
        } catch (error) {
            console.error("Failed to fetch suppliers:", error);
        } finally {
            setSuppliersLoading(false);
        }
    };

    useEffect(() => {
        if (tabValue === 2) {
            fetchSuppliers();
        }
    }, [tabValue]);

    const fetchInventoryItems = async () => {
        setInventoryItemsLoading(true);
        try {
            const data = await InventoryService.getInventoryItems();
            setInventoryItems(data || []);
        } catch (error) {
            console.error("Failed to fetch inventory items:", error);
        } finally {
            setInventoryItemsLoading(false);
        }
    };

    useEffect(() => {
        if (tabValue === 0) {
            fetchInventoryItems();
        }
    }, [tabValue]);

    const fetchPurchaseOrders = async () => {
        setPurchaseOrdersLoading(true);
        try {
            const data = await InventoryService.getPurchaseOrders();
            setPurchaseOrders(data || []);
        } catch (error) {
            console.error("Failed to fetch purchase orders:", error);
        } finally {
            setPurchaseOrdersLoading(false);
        }
    };

    useEffect(() => {
        if (tabValue === 1) {
            fetchPurchaseOrders();
            fetchPaymentMethods();
        }
    }, [tabValue]);

    useEffect(() => {
        if (inventoryItemDrawerOpen && selectedInventoryItemId) {
            const fetchItemDetails = async () => {
                setInventoryItemDetailsLoading(true);
                try {
                    const data = await InventoryService.getInventoryItemById(selectedInventoryItemId);
                    setInventoryItemDetails(data);
                } catch (error) {
                    console.error("Failed to fetch inventory item details:", error);
                } finally {
                    setInventoryItemDetailsLoading(false);
                }
            };
            fetchItemDetails();
        } else {
            setInventoryItemDetails(null);
        }
    }, [inventoryItemDrawerOpen, selectedInventoryItemId]);

    useEffect(() => {
        if (poDrawerOpen && selectedPoId) {
            const fetchPoDetails = async () => {
                setPoDetailsLoading(true);
                try {
                    const data = await InventoryService.getPurchaseOrderById(selectedPoId);
                    setPoDetails(data);
                } catch (error) {
                    console.error("Failed to fetch purchase order details:", error);
                } finally {
                    setPoDetailsLoading(false);
                }
            };
            fetchPoDetails();
        } else {
            setPoDetails(null);
        }
    }, [poDrawerOpen, selectedPoId]);

    useEffect(() => {
        if (inventoryItemDrawerOpen && selectedInventoryItemId) {
            const fetchMovements = async () => {
                setItemMovementsLoading(true);
                try {
                    const res = await InventoryService.getInventoryItemMovements(selectedInventoryItemId, {
                        page: movementsPagination.page,
                        limit: movementsPagination.limit
                    });
                    setItemMovements(res?.data || []);
                    if (res?.pagination) {
                        setMovementsPagination(prev => ({
                            ...prev,
                            totalPages: res.pagination.totalPages || 1
                        }));
                    }
                } catch (error) {
                    console.error("Failed to fetch inventory item movements:", error);
                } finally {
                    setItemMovementsLoading(false);
                }
            };
            fetchMovements();
        } else {
            setItemMovements([]);
            setMovementsPagination({ page: 1, limit: 10, totalPages: 1 });
        }
    }, [inventoryItemDrawerOpen, selectedInventoryItemId, movementsPagination.page, movementsPagination.limit]);

    useEffect(() => {
        if (detailDrawerOpen && selectedSupplierId) {
            const fetchDetails = async () => {
                setDetailsLoading(true);
                try {
                    const data = await SupplierService.getSupplierById(selectedSupplierId);
                    setSupplierDetails(data);
                } catch (error) {
                    console.error("Failed to fetch supplier details:", error);
                } finally {
                    setDetailsLoading(false);
                }
            };
            fetchDetails();
        } else {
            setSupplierDetails(null);
        }
    }, [detailDrawerOpen, selectedSupplierId]);

    const handleCreateInventoryItem = async () => {
        if (!inventoryItemForm.name?.trim()) {
            toast.error("Item name is required");
            return;
        }
        if (!inventoryItemForm.category) {
            toast.error("Category is required");
            return;
        }
        if (!inventoryItemForm.unitOfMeasure) {
            toast.error("Unit of measure is required");
            return;
        }

        setIsSubmittingInventoryItem(true);
        try {
            const payload = {
                name: inventoryItemForm.name.trim(),
                category: inventoryItemForm.category,
                unitOfMeasure: inventoryItemForm.unitOfMeasure,
            };

            if (inventoryItemForm.currentStock !== '' && !isNaN(Number(inventoryItemForm.currentStock)) && Number(inventoryItemForm.currentStock) > 0) {
                payload.currentStock = Number(inventoryItemForm.currentStock);
            }
            if (inventoryItemForm.reorderLevel !== '' && !isNaN(Number(inventoryItemForm.reorderLevel)) && Number(inventoryItemForm.reorderLevel) > 0) {
                payload.reorderLevel = Number(inventoryItemForm.reorderLevel);
            }
            if (inventoryItemForm.maxStock !== '' && !isNaN(Number(inventoryItemForm.maxStock)) && Number(inventoryItemForm.maxStock) > 0) {
                payload.maxStock = Number(inventoryItemForm.maxStock);
            }
            if (inventoryItemForm.unitCost !== '' && !isNaN(Number(inventoryItemForm.unitCost)) && Number(inventoryItemForm.unitCost) > 0) {
                payload.unitCost = Number(inventoryItemForm.unitCost);
            }
            if (inventoryItemForm.supplierId) {
                payload.supplierId = inventoryItemForm.supplierId;
            }

            await InventoryService.createInventoryItem(payload);
            toast.success("New inventory item successfully indexed into the master ledger");
            setInventoryItemModalOpen(false);
            setInventoryItemForm({
                name: '',
                category: '',
                unitOfMeasure: '',
                currentStock: '',
                reorderLevel: '',
                maxStock: '',
                unitCost: '',
                supplierId: ''
            });
            await fetchInventoryItems();
            setItemsRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to create inventory item:", error);
        } finally {
            setIsSubmittingInventoryItem(false);
        }
    };

    const handleViewInventoryItem = (id) => {
        setSelectedInventoryItemId(id);
        setMovementsPagination({ page: 1, limit: 10, totalPages: 1 });
        setInventoryItemDrawerOpen(true);
    };

    const handleViewPO = (id) => {
        setSelectedPoId(id);
        setPoDrawerOpen(true);
    };

    const handleOpenEditInventoryItem = (item) => {
        setSelectedEditInventoryItemId(item.id);
        setEditInventoryItemForm({
            name: item.name || item.sku || '',
            unitCost: item.unitCost || 0
        });
        setEditInventoryItemModalOpen(true);
    };

    const handleUpdateInventoryItem = async () => {
        setIsUpdatingInventoryItem(true);
        try {
            await InventoryService.updateInventoryItem(selectedEditInventoryItemId, {
                name: editInventoryItemForm.name,
                unitCost: Number(editInventoryItemForm.unitCost)
            });
            toast.success("Inventory item details updated successfully");
            setEditInventoryItemModalOpen(false);
            setEditInventoryItemForm({ name: '', unitCost: 0 });
            setSelectedEditInventoryItemId(null);
            await fetchInventoryItems();
            setItemsRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to update inventory item:", error);
        } finally {
            setIsUpdatingInventoryItem(false);
        }
    };

    const handleOpenDeleteInventoryItem = (id) => {
        setSelectedDeleteInventoryItemId(id);
        setDeleteInventoryItemModalOpen(true);
    };

    const handleDeleteInventoryItem = async () => {
        setIsDeletingInventoryItem(true);
        try {
            await InventoryService.deleteInventoryItem(selectedDeleteInventoryItemId);
            toast.success("Inventory item permanently removed from directory");
            setDeleteInventoryItemModalOpen(false);
            setSelectedDeleteInventoryItemId(null);
            await fetchInventoryItems();
            setItemsRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to delete inventory item:", error);
        } finally {
            setIsDeletingInventoryItem(false);
        }
    };

    const handleOpenAdjustStock = (itemOrId) => {
        const item = (typeof itemOrId === 'object' && itemOrId !== null)
            ? itemOrId
            : (inventoryItemDetails?.id === itemOrId ? inventoryItemDetails : inventoryItems.find(i => i.id === itemOrId));
        setSelectedAdjustStockItemId(item?.id || itemOrId);
        setSelectedAdjustStockItem(item || null);
        setAdjustStockForm({
            quantity: '',
            reason: '',
            notes: ''
        });
        setAdjustStockModalOpen(true);
    };

    const handleAdjustStock = async () => {
        setIsAdjustingStock(true);
        try {
            await InventoryService.adjustInventoryItemStock(selectedAdjustStockItemId, {
                quantity: Number(adjustStockForm.quantity),
                reason: adjustStockForm.reason,
                notes: adjustStockForm.notes
            });
            toast.success("Stock count adjustment processed and ledger updated successfully");
            setAdjustStockModalOpen(false);
            setAdjustStockForm({ quantity: '', reason: '', notes: '' });
            
            await fetchInventoryItems();
            setItemsRefreshKey(prev => prev + 1);
            
            // If the details drawer is open for this item, refresh the details and movements
            if (inventoryItemDrawerOpen && selectedInventoryItemId === selectedAdjustStockItemId) {
                const fetchItemDetails = async () => {
                    setInventoryItemDetailsLoading(true);
                    try {
                        const data = await InventoryService.getInventoryItemById(selectedAdjustStockItemId);
                        setInventoryItemDetails(data);
                    } catch (error) {
                        console.error("Failed to fetch inventory item details:", error);
                    } finally {
                        setInventoryItemDetailsLoading(false);
                    }
                };
                fetchItemDetails();
                
                const fetchMovements = async () => {
                    setItemMovementsLoading(true);
                    try {
                        const res = await InventoryService.getInventoryItemMovements(selectedAdjustStockItemId, {
                            page: 1,
                            limit: movementsPagination.limit
                        });
                        setItemMovements(res?.data || []);
                        if (res?.pagination) {
                            setMovementsPagination(prev => ({
                                ...prev,
                                page: 1,
                                totalPages: res.pagination.totalPages || 1
                            }));
                        }
                    } catch (error) {
                        console.error("Failed to fetch inventory item movements:", error);
                    } finally {
                        setItemMovementsLoading(false);
                    }
                };
                fetchMovements();
            }
            setSelectedAdjustStockItemId(null);
        } catch (error) {
            console.error("Failed to adjust inventory item stock:", error);
            const errMsg = error?.response?.data?.message || error?.message || "Failed to adjust inventory item stock";
            toast.error(errMsg);
        } finally {
            setIsAdjustingStock(false);
        }
    };

    const handleOpenCreatePO = async () => {
        setCreatePOModalOpen(true);
        if (suppliers.length === 0) fetchSuppliers();
        if (inventoryItems.length === 0) fetchInventoryItems();
    };

    const handleCreatePO = async () => {
        if (!poForm.supplierId) {
            toast.error("Please select a supplier");
            return;
        }
        if (!poForm.expectedDeliveryDate) {
            toast.error("Expected delivery date is required");
            return;
        }
        if (!poForm.items || poForm.items.length === 0) {
            toast.error("At least one line item is required");
            return;
        }
        for (let i = 0; i < poForm.items.length; i++) {
            const item = poForm.items[i];
            if (!item.itemId) {
                toast.error(`Please select an inventory item for line ${i + 1}`);
                return;
            }
            if (!item.quantityOrdered || Number(item.quantityOrdered) <= 0) {
                toast.error(`Quantity ordered must be greater than 0 for line ${i + 1}`);
                return;
            }
            if (item.unitCost === '' || isNaN(Number(item.unitCost)) || Number(item.unitCost) <= 0) {
                toast.error(`Unit cost must be greater than 0 for line ${i + 1}`);
                return;
            }
        }

        setIsCreatingPO(true);
        try {
            const formattedDate = new Date(poForm.expectedDeliveryDate).toISOString();
            const formattedItems = poForm.items.map(item => ({
                itemId: item.itemId,
                quantityOrdered: Number(item.quantityOrdered),
                unitCost: Number(item.unitCost)
            }));
            
            await InventoryService.createPurchaseOrder({
                supplierId: poForm.supplierId,
                expectedDeliveryDate: formattedDate,
                notes: poForm.notes,
                items: formattedItems
            });
            
            toast.success("Purchase order created and queued successfully");
            setCreatePOModalOpen(false);
            setPoForm({
                supplierId: '',
                expectedDeliveryDate: '',
                notes: '',
                items: [{ itemId: '', quantityOrdered: '', unitCost: '' }]
            });
            await fetchPurchaseOrders();
            setPosRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to create purchase order:", error);
        } finally {
            setIsCreatingPO(false);
        }
    };

    const handleSubmitPO = async () => {
        if (!poDetails) return;
        setIsSubmittingPO(true);
        try {
            await InventoryService.submitPurchaseOrder(poDetails.id);
            toast.success("Purchase order submitted successfully");
            
            // Re-fetch details to update the badge to active marker
            const data = await InventoryService.getPurchaseOrderById(poDetails.id);
            setPoDetails(data);
            
            // Also refresh the parent grid
            await fetchPurchaseOrders();
            setPosRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to submit purchase order:", error);
        } finally {
            setIsSubmittingPO(false);
        }
    };

    const handleApprovePO = async () => {
        if (!poDetails) return;
        setIsReviewingPO(true);
        try {
            await InventoryService.approveRejectPurchaseOrder(poDetails.id, {
                status: 'Approved',
                rejectionReason: ''
            });
            toast.success("Purchase order successfully approved");
            setPoApproveModalOpen(false);
            
            // Re-fetch details to update the badge to active marker
            const data = await InventoryService.getPurchaseOrderById(poDetails.id);
            setPoDetails(data);
            
            // Also refresh the parent grid
            await fetchPurchaseOrders();
            setPosRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to approve purchase order:", error);
        } finally {
            setIsReviewingPO(false);
        }
    };

    const handleRejectPO = async () => {
        if (!poDetails) return;
        setIsReviewingPO(true);
        try {
            await InventoryService.approveRejectPurchaseOrder(poDetails.id, {
                status: 'Rejected',
                rejectionReason: poRejectReason
            });
            toast.success("Purchase order marked as rejected");
            
            setPoRejectModalOpen(false);
            setPoRejectReason('');
            
            // Re-fetch details to update the badge to active marker
            const data = await InventoryService.getPurchaseOrderById(poDetails.id);
            setPoDetails(data);
            
            // Also refresh the parent grid
            await fetchPurchaseOrders();
            setPosRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to reject purchase order:", error);
        } finally {
            setIsReviewingPO(false);
        }
    };

    const handleOpenReceivePO = () => {
        if (!poDetails || !poDetails.items) return;
        setPoReceiveForm({
            supplierInvoiceNumber: '',
            items: poDetails.items.map(item => ({
                itemId: item.itemId,
                itemName: item.itemName || (item.item ? item.item.name || item.item.sku : 'Unknown Item'),
                quantityExpected: item.quantityOrdered || item.quantity || 0,
                quantityReceived: item.quantityOrdered || item.quantity || 0 // Pre-populated with expected
            }))
        });
        setPoReceiveModalOpen(true);
    };

    const handleReceivePO = async () => {
        if (!poDetails) return;
        if (!poReceiveForm.supplierInvoiceNumber?.trim()) {
            toast.error("Supplier Invoice / Reference Number is required");
            return;
        }
        setIsReceivingPO(true);
        try {
            await InventoryService.receivePurchaseOrder(poDetails.id, {
                supplierInvoiceNumber: poReceiveForm.supplierInvoiceNumber.trim(),
                items: poReceiveForm.items.map(item => ({
                    itemId: item.itemId,
                    quantityReceived: Number(item.quantityReceived)
                }))
            });
            toast.success("Delivery receipt recorded successfully; inventory stock levels updated");
            
            setPoReceiveModalOpen(false);
            setPoReceiveForm({ supplierInvoiceNumber: '', items: [] });
            
            // Re-fetch details to update the badge to active marker
            const data = await InventoryService.getPurchaseOrderById(poDetails.id);
            setPoDetails(data);
            
            // Also refresh the parent grid
            await fetchPurchaseOrders();
            // Refresh inventory items since stock is updated
            await fetchInventoryItems();
            setPosRefreshKey(prev => prev + 1);
            setItemsRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to receive purchase order:", error);
        } finally {
            setIsReceivingPO(false);
        }
    };

    const handleOpenPOPayment = (poOrEvent) => {
        const targetPo = (poOrEvent && typeof poOrEvent === 'object' && poOrEvent.id)
            ? poOrEvent
            : poDetails;
        if (!targetPo) return;

        fetchPaymentMethods();
        setSelectedPOForPayment(targetPo);

        const supplierName = targetPo.supplierName || (targetPo.supplier ? targetPo.supplier.companyName || targetPo.supplier.name : '');
        const amount = targetPo.totalAmount || targetPo.totalCost || targetPo.totalValue || targetPo.orderValue || '';

        const now = new Date();
        const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

        setPoPaymentForm({
            paymentMethod: 'Bank Transfer',
            amount: amount ? Number(amount) : '',
            paymentReference: '',
            category: 'Inventory & Supplies',
            paymentDate: localIso,
            notes: supplierName ? `Paid ${supplierName}` : ''
        });

        setPoPaymentModalOpen(true);
    };

    const handleRecordPOPayment = async () => {
        if (!selectedPOForPayment) return;

        if (!poPaymentForm.paymentMethod) {
            toast.error("Payment method is required");
            return;
        }
        if (!poPaymentForm.amount || Number(poPaymentForm.amount) <= 0) {
            toast.error("Valid payment amount is required");
            return;
        }
        if (!poPaymentForm.paymentReference?.trim()) {
            toast.error("Payment reference is required");
            return;
        }

        setIsRecordingPOPayment(true);
        try {
            let isoDate = new Date().toISOString();
            if (poPaymentForm.paymentDate) {
                const parsed = new Date(poPaymentForm.paymentDate);
                if (!isNaN(parsed.getTime())) {
                    isoDate = parsed.toISOString();
                }
            }

            const payload = {
                paymentMethod: poPaymentForm.paymentMethod,
                amount: Number(poPaymentForm.amount),
                paymentReference: poPaymentForm.paymentReference.trim(),
                category: poPaymentForm.category?.trim() || 'Inventory & Supplies',
                paymentDate: isoDate,
                notes: poPaymentForm.notes?.trim() || ''
            };

            await InventoryService.recordPOPayment(selectedPOForPayment.id, payload);
            toast.success("Payment recorded successfully");

            setPoPaymentModalOpen(false);
            setPoPaymentForm({
                paymentMethod: 'Bank Transfer',
                amount: '',
                paymentReference: '',
                category: 'Inventory & Supplies',
                paymentDate: '',
                notes: ''
            });

            // Re-fetch details if drawer is open for this PO
            if (selectedPoId === selectedPOForPayment.id || poDetails?.id === selectedPOForPayment.id) {
                const updatedPo = await InventoryService.getPurchaseOrderById(selectedPOForPayment.id);
                setPoDetails(updatedPo);
            }

            // Refresh purchase orders list
            await fetchPurchaseOrders();
            setPosRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to record purchase order payment:", error);
            const errMsg = error?.response?.data?.message || error?.message || "Failed to record purchase order payment";
            toast.error(errMsg);
        } finally {
            setIsRecordingPOPayment(false);
        }
    };

    const handleAddPOLineItem = () => {
        setPoForm({ ...poForm, items: [...poForm.items, { itemId: '', quantityOrdered: '', unitCost: '' }] });
    };

    const stats = {
        totalItems: inventoryItems.length,
        lowStock: inventoryItems.filter(item => item.lowStockWarning || (item.currentStock > 0 && item.currentStock <= item.reorderLevel)).length,
        outOfStock: inventoryItems.filter(item => item.outOfStock || item.currentStock === 0).length,
        pendingPOs: purchaseOrders.filter(po => po.status !== 'Received' && po.status !== 'Delivered' && po.status !== 'Rejected').length
    };

    const lowStockItemsList = inventoryItems.filter(item => item.outOfStock || item.lowStockWarning || item.currentStock <= item.reorderLevel);

    const getDepartmentCapacity = (dept) => {
        const deptItems = inventoryItems.filter(i => (i.category || 'General') === dept);
        if (deptItems.length === 0) return 0;
        let current = 0;
        let max = 0;
        deptItems.forEach(i => {
            current += (i.currentStock !== undefined ? i.currentStock : (i.stockQuantity || i.stock || 0));
            max += (i.maxStock || (i.reorderLevel || 0) * 2 || 100);
        });
        if (max === 0) return 0;
        return Math.min(Math.round((current / max) * 100), 100);
    };

    const handleExportMovements = () => {
        if (!itemMovements || itemMovements.length === 0) {
            toast.info("No movement history to export.");
            return;
        }
        const csvRows = [];
        const headers = ['Date', 'Type', 'Quantity', 'Reason', 'Notes'];
        csvRows.push(headers.join(','));

        for (const row of itemMovements) {
            const values = [
                `"${new Date(row.createdAt || row.date).toLocaleString()}"`,
                `"${row.type || 'Adjustment'}"`,
                `"${row.quantity}"`,
                `"${row.reason || 'N/A'}"`,
                `"${row.notes || ''}"`
            ];
            csvRows.push(values.join(','));
        }

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', 'movement_history.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleRemovePOLineItem = (index) => {
        const newItems = [...poForm.items];
        newItems.splice(index, 1);
        setPoForm({ ...poForm, items: newItems });
    };

    const handlePOLineItemChange = (index, field, value) => {
        const newItems = [...poForm.items];
        newItems[index][field] = value;
        if (field === 'itemId') {
            const selectedItem = inventoryItems.find(i => i.id === value);
            if (selectedItem && selectedItem.unitCost !== undefined && selectedItem.unitCost !== null && Number(selectedItem.unitCost) > 0) {
                newItems[index].unitCost = selectedItem.unitCost;
            }
        }
        setPoForm({ ...poForm, items: newItems });
    };

    const handleCreateSupplier = async () => {
        if (!supplierForm.companyName?.trim()) {
            toast.error("Company name is required");
            return;
        }
        if (!supplierForm.contactPerson?.trim()) {
            toast.error("Contact person is required");
            return;
        }
        if (!supplierForm.email?.trim()) {
            toast.error("Email address is required");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierForm.email.trim())) {
            toast.error("Please enter a valid email address (e.g., name@company.com)");
            return;
        }
        if (!supplierForm.phone?.trim()) {
            toast.error("Phone number is required");
            return;
        }
        if (supplierForm.phone.length !== 10) {
            toast.error("Phone number must be exactly 10 digits");
            return;
        }
        if (!supplierForm.category) {
            toast.error("Category is required");
            return;
        }
        if (!supplierForm.paymentTerms) {
            toast.error("Payment terms are required");
            return;
        }
        setIsSubmittingSupplier(true);
        try {
            await SupplierService.createSupplier(supplierForm);
            toast.success("New supplier record successfully indexed into vendor registry");
            setSupplierModalOpen(false);
            setSupplierForm({
                companyName: '',
                contactPerson: '',
                email: '',
                phone: '',
                category: '',
                paymentTerms: ''
            });
            await fetchSuppliers();
            setSuppliersRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to create supplier:", error);
        } finally {
            setIsSubmittingSupplier(false);
        }
    };

    const handleOpenEditSupplier = (supplier) => {
        setSelectedEditSupplierId(supplier.id);
        setEditSupplierForm({
            contactPerson: supplier.contactPerson || supplier.contact || '',
            email: supplier.email || ''
        });
        setEditSupplierModalOpen(true);
    };

    const handleUpdateSupplier = async () => {
        if (!editSupplierForm.contactPerson?.trim()) {
            toast.error("Contact person is required");
            return;
        }
        if (!editSupplierForm.email?.trim()) {
            toast.error("Email address is required");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editSupplierForm.email.trim())) {
            toast.error("Please enter a valid email address (e.g., name@company.com)");
            return;
        }
        setIsUpdatingSupplier(true);
        try {
            await SupplierService.updateSupplier(selectedEditSupplierId, editSupplierForm);
            toast.success("Supplier contact details updated successfully");
            setEditSupplierModalOpen(false);
            setEditSupplierForm({ contactPerson: '', email: '' });
            setSelectedEditSupplierId(null);
            await fetchSuppliers();
            setSuppliersRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to update supplier:", error);
        } finally {
            setIsUpdatingSupplier(false);
        }
    };

    const handleOpenDeleteSupplier = (id) => {
        setSelectedDeleteSupplierId(id);
        setDeleteSupplierModalOpen(true);
    };

    const handleDeleteSupplier = async () => {
        setIsDeletingSupplier(true);
        try {
            await SupplierService.deleteSupplier(selectedDeleteSupplierId);
            toast.success("Supplier record permanently removed from directory");
            setDeleteSupplierModalOpen(false);
            setSelectedDeleteSupplierId(null);
            await fetchSuppliers();
            setSuppliersRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error("Failed to delete supplier:", error);
        } finally {
            setIsDeletingSupplier(false);
        }
    };

    const handleViewSupplier = (id) => {
        setSelectedSupplierId(id);
        setDetailDrawerOpen(true);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const getStockColor = (stock, reorder) => {
        if (stock <= 0) return 'error';
        if (stock <= reorder) return 'warning';
        return 'success';
    };

    const getPOStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'primary';
            case 'Submitted': return 'warning';
            case 'Delivered': return 'success';
            case 'Draft': return 'default';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Inventory & Store</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                        onClick={handleOpenCreatePO}
                    >
                        Raise PO
                    </Button>
                </Box>
            </Box>

            {isAuditMode && (
                <Alert severity="warning" sx={{ mb: 4 }}>
                    <b>Audit Mode Active:</b> Automated inventory stock deductions linked to room usage or housekeeping are currently paused due to property status. All inventory adjustments must be made manually.
                </Alert>
            )}

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            TOTAL ITEMS
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {stats.totalItems}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Catalogue inventory
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            LOW STOCK
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#d97706', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {stats.lowStock}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Below reorder threshold
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            OUT OF STOCK
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {stats.outOfStock}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Depleted items
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                            PENDING POS
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#4146e0', fontSize: '1.85rem', lineHeight: 1.1 }}>
                            {stats.pendingPOs}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                            Awaiting fulfillment
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>Stock Capacity by Department</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {['Kitchen', 'Room Supplies', 'Housekeeping', 'Maintenance'].map(dept => {
                                const cap = getDepartmentCapacity(dept);
                                return (
                                    <Box key={dept}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2">{dept}</Typography>
                                            <Typography variant="body2" fontWeight="bold">{cap}%</Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={cap} color={cap < 20 ? 'error' : cap < 50 ? 'warning' : 'success'} sx={{ height: 8, borderRadius: 4 }} />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #eaecf0', boxShadow: 'none', bgcolor: '#ffffff', height: '100%', maxHeight: 260, overflowY: 'auto' }}>
                        <Typography variant="h6" fontWeight="bold" mb={2} color="error.main">Low Stock Alerts</Typography>
                        {lowStockItemsList.length === 0 ? (
                            <Typography color="text.secondary">No active alerts.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item Name</TableCell>
                                        <TableCell align="right">Current</TableCell>
                                        <TableCell align="right">Reorder</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lowStockItemsList.map(item => (
                                        <TableRow key={item.id}>
                                            <TableCell><Typography variant="body2">{item.name || item.sku}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" color="error.main" fontWeight="bold">{item.currentStock}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2">{item.reorderLevel}</Typography></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                    <Tab label="Stock Catalogue" />
                    <Tab label="Purchase Orders" />
                    <Tab label="Suppliers" />
                </Tabs>
            </Box>

            <Box>
                    {/* Stock Catalogue */}
                    <TabPanel value={tabValue} index={0}>
                        <StockCatalogueTab 
                            refreshKey={itemsRefreshKey}
                            setInventoryItemModalOpen={setInventoryItemModalOpen}
                            inventoryItemsLoading={inventoryItemsLoading}
                            inventoryItems={inventoryItems}
                            handleViewInventoryItem={handleViewInventoryItem}
                            handleOpenEditInventoryItem={handleOpenEditInventoryItem}
                            handleOpenDeleteInventoryItem={handleOpenDeleteInventoryItem}
                        />
                    </TabPanel>

                    {/* Purchase Orders */}
                    <TabPanel value={tabValue} index={1}>
                        <PurchaseOrdersTab 
                            refreshKey={posRefreshKey}
                            purchaseOrdersLoading={purchaseOrdersLoading}
                            purchaseOrders={purchaseOrders}
                            getPOStatusColor={getPOStatusColor}
                            handleViewPO={handleViewPO}
                            handleOpenPOPayment={handleOpenPOPayment}
                        />
                    </TabPanel>

                    {/* Suppliers */}
                    <TabPanel value={tabValue} index={2}>
                        <SuppliersTab 
                            refreshKey={suppliersRefreshKey}
                            setSupplierModalOpen={setSupplierModalOpen}
                            suppliersLoading={suppliersLoading}
                            suppliers={suppliers}
                            handleOpenEditSupplier={handleOpenEditSupplier}
                            handleViewSupplier={handleViewSupplier}
                            handleOpenDeleteSupplier={handleOpenDeleteSupplier}
                        />
                    </TabPanel>
                </Box>

            <InventoryItemModals
                inventoryItemModalOpen={inventoryItemModalOpen}
                setInventoryItemModalOpen={setInventoryItemModalOpen}
                isSubmittingInventoryItem={isSubmittingInventoryItem}
                inventoryItemForm={inventoryItemForm}
                setInventoryItemForm={setInventoryItemForm}
                handleCreateInventoryItem={handleCreateInventoryItem}
                editInventoryItemModalOpen={editInventoryItemModalOpen}
                setEditInventoryItemModalOpen={setEditInventoryItemModalOpen}
                isUpdatingInventoryItem={isUpdatingInventoryItem}
                editInventoryItemForm={editInventoryItemForm}
                setEditInventoryItemForm={setEditInventoryItemForm}
                handleUpdateInventoryItem={handleUpdateInventoryItem}
                deleteInventoryItemModalOpen={deleteInventoryItemModalOpen}
                setDeleteInventoryItemModalOpen={setDeleteInventoryItemModalOpen}
                isDeletingInventoryItem={isDeletingInventoryItem}
                handleDeleteInventoryItem={handleDeleteInventoryItem}
                adjustStockModalOpen={adjustStockModalOpen}
                setAdjustStockModalOpen={setAdjustStockModalOpen}
                isAdjustingStock={isAdjustingStock}
                adjustStockForm={adjustStockForm}
                setAdjustStockForm={setAdjustStockForm}
                handleAdjustStock={handleAdjustStock}
                selectedAdjustStockItem={selectedAdjustStockItem}
                suppliers={suppliers}
            />
            <SupplierModals 
                supplierModalOpen={supplierModalOpen}
                setSupplierModalOpen={setSupplierModalOpen}
                isSubmittingSupplier={isSubmittingSupplier}
                supplierForm={supplierForm}
                setSupplierForm={setSupplierForm}
                handleCreateSupplier={handleCreateSupplier}
                editSupplierModalOpen={editSupplierModalOpen}
                setEditSupplierModalOpen={setEditSupplierModalOpen}
                isUpdatingSupplier={isUpdatingSupplier}
                editSupplierForm={editSupplierForm}
                setEditSupplierForm={setEditSupplierForm}
                handleUpdateSupplier={handleUpdateSupplier}
                deleteSupplierModalOpen={deleteSupplierModalOpen}
                setDeleteSupplierModalOpen={setDeleteSupplierModalOpen}
                isDeletingSupplier={isDeletingSupplier}
                handleDeleteSupplier={handleDeleteSupplier}
            />

            {/* Inventory Item Details Drawer */}
            <Drawer
                anchor="right"
                open={inventoryItemDrawerOpen}
                onClose={() => setInventoryItemDrawerOpen(false)}
            >
                <Box sx={{ width: { xs: '100vw', sm: 500, md: 600 }, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>Inventory Item Profile</Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    {inventoryItemDetailsLoading ? (
                        <Box>
                            <Skeleton variant="text" width="60%" height={40} mb={1} />
                            <Skeleton variant="text" width="40%" height={30} sx={{ mb: 3 }} />
                            
                            <Typography variant="subtitle2" color="text.secondary" mb={1}>Stock Metrics</Typography>
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2, mb: 3 }} />

                            <Typography variant="subtitle2" color="text.secondary" mb={1}>Financial Parameters</Typography>
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2 }} />
                        </Box>
                    ) : inventoryItemDetails ? (
                        <Box>
                            {/* Vendor Meta Header */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" fontWeight="bold" mb={1}>
                                    {inventoryItemDetails.name || inventoryItemDetails.sku || 'Unknown Item'}
                                </Typography>
                                {inventoryItemDetails.sku && (
                                    <Typography variant="body2" color="text.secondary" mb={1}>
                                        SKU: {inventoryItemDetails.sku}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip label={inventoryItemDetails.department || inventoryItemDetails.category || 'General'} size="small" color="primary" variant="outlined" />
                                    <Chip 
                                        label={inventoryItemDetails.status || 'Active'} 
                                        size="small" 
                                        color={(inventoryItemDetails.status || 'Active').toLowerCase() === 'active' ? 'success' : 'warning'} 
                                    />
                                </Box>
                            </Box>

                            {/* Stock Metrics Fields */}
                            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">STOCK LEVEL METRICS</Typography>
                                    <Button 
                                        variant="outlined" 
                                        color="primary"
                                        size="small"
                                        onClick={() => handleOpenAdjustStock(inventoryItemDetails.id)}
                                    >
                                        Adjust Stock
                                    </Button>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Current Stock On Hand</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {inventoryItemDetails.currentStock !== undefined ? inventoryItemDetails.currentStock : (inventoryItemDetails.stockQuantity || inventoryItemDetails.stock || 0)} {inventoryItemDetails.unitOfMeasure}
                                            </Typography>
                                            <Chip 
                                                label={inventoryItemDetails.outOfStock ? "Empty" : inventoryItemDetails.lowStockWarning ? "Low" : "OK"} 
                                                size="small" 
                                                color={inventoryItemDetails.outOfStock ? "error" : inventoryItemDetails.lowStockWarning ? "warning" : "success"}
                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Reorder Level Threshold</Typography>
                                        <Typography variant="body2">{inventoryItemDetails.reorderLevel || 0}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Maximum Capacity</Typography>
                                        <Typography variant="body2">{inventoryItemDetails.maxStock || 0}</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Financial Parameters Summary */}
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Typography variant="subtitle2" color="text.secondary" mb={2}>FINANCIAL METRICS</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Unit Cost</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency(inventoryItemDetails.unitCost || 0, currency)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Total Asset Value</Typography>
                                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                                            {formatCurrency(((inventoryItemDetails.currentStock !== undefined ? inventoryItemDetails.currentStock : (inventoryItemDetails.stockQuantity || inventoryItemDetails.stock || 0))) * (inventoryItemDetails.unitCost || 0), currency)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Movement History Ledger */}
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">STOCK ACTIVITY LOG</Typography>
                                    <Button 
                                        variant="text" 
                                        size="small" 
                                        onClick={handleExportMovements}
                                        disabled={!itemMovements || itemMovements.length === 0}
                                    >
                                        Export CSV
                                    </Button>
                                </Box>
                                {itemMovementsLoading ? (
                                    <Box display="flex" justifyContent="center" p={3}>
                                        <CircularProgress size={30} />
                                    </Box>
                                ) : (
                                    <>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell align="right">Qty</TableCell>
                                                        <TableCell>Reason & Notes</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {itemMovements.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center">No movement history found.</TableCell>
                                                        </TableRow>
                                                    ) : itemMovements.map((log) => (
                                                        <TableRow key={log.id}>
                                                            <TableCell><Typography variant="caption">{new Date(log.createdAt || log.date).toLocaleString()}</Typography></TableCell>
                                                            <TableCell>
                                                                <Chip 
                                                                    label={log.type || 'Adjustment'} 
                                                                    size="small" 
                                                                    color={log.type === 'Stock In' ? 'success' : log.type === 'Stock Out' ? 'error' : 'default'}
                                                                    sx={{ fontSize: '0.65rem' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color={log.quantity > 0 ? 'success.main' : log.quantity < 0 ? 'error.main' : 'text.primary'} fontWeight="bold">
                                                                    {log.quantity > 0 ? `+${log.quantity}` : log.quantity}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{log.reason || 'N/A'}</Typography>
                                                                {log.notes && <Typography variant="caption" color="text.secondary" display="block">{log.notes}</Typography>}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                            <FormControl size="small" variant="standard" sx={{ minWidth: 80 }}>
                                                <Select
                                                    value={movementsPagination.limit}
                                                    onChange={(e) => setMovementsPagination({ ...movementsPagination, limit: Number(e.target.value), page: 1 })}
                                                    disableUnderline
                                                >
                                                    <MenuItem value={5}>5 Rows</MenuItem>
                                                    <MenuItem value={10}>10 Rows</MenuItem>
                                                    <MenuItem value={25}>25 Rows</MenuItem>
                                                    <MenuItem value={100}>100 Rows</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button 
                                                    size="small" 
                                                    disabled={movementsPagination.page <= 1}
                                                    onClick={() => setMovementsPagination({ ...movementsPagination, page: movementsPagination.page - 1 })}
                                                >
                                                    Previous Page
                                                </Button>
                                                <Button 
                                                    size="small" 
                                                    disabled={movementsPagination.page >= movementsPagination.totalPages}
                                                    onClick={() => setMovementsPagination({ ...movementsPagination, page: movementsPagination.page + 1 })}
                                                >
                                                    Next Page
                                                </Button>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Paper>
                        </Box>
                    ) : (
                        <Typography color="text.secondary">Inventory item details not found.</Typography>
                    )}
                </Box>
            </Drawer>

            {/* Supplier Details Drawer */}
            <Drawer
                anchor="right"
                open={detailDrawerOpen}
                onClose={() => setDetailDrawerOpen(false)}
            >
                <Box sx={{ width: { xs: '100vw', sm: 400 }, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>Supplier Profile</Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    {detailsLoading ? (
                        <Box>
                            <Skeleton variant="text" width="60%" height={40} mb={1} />
                            <Skeleton variant="text" width="40%" height={30} sx={{ mb: 3 }} />
                            
                            <Typography variant="subtitle2" color="text.secondary" mb={1}>Contact Information</Typography>
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2, mb: 3 }} />

                            <Typography variant="subtitle2" color="text.secondary" mb={1}>Financial Parameters</Typography>
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: 2 }} />
                        </Box>
                    ) : supplierDetails ? (
                        <Box>
                            {/* Vendor Meta Header */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h5" fontWeight="bold" mb={1}>
                                    {supplierDetails.companyName || supplierDetails.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip label={supplierDetails.category || 'General'} size="small" color="primary" variant="outlined" />
                                    <Chip 
                                        label={supplierDetails.status || 'Active'} 
                                        size="small" 
                                        color={(supplierDetails.status || 'Active').toLowerCase() === 'active' ? 'success' : 'warning'} 
                                    />
                                </Box>
                            </Box>

                            {/* Corporate Contact Fields */}
                            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Typography variant="subtitle2" color="text.secondary" mb={2}>CORPORATE CONTACT</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Contact Person</Typography>
                                        <Typography variant="body2" fontWeight="bold">{supplierDetails.contactPerson || supplierDetails.contact || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Email Address</Typography>
                                        <Typography variant="body2">{supplierDetails.email || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                                        <Typography variant="body2">{supplierDetails.phone || 'N/A'}</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Financial Parameters Summary */}
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Typography variant="subtitle2" color="text.secondary" mb={2}>FINANCIAL SUMMARY</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Payment Terms</Typography>
                                        <Typography variant="body2" fontWeight="bold">{supplierDetails.paymentTerms || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Account Balance</Typography>
                                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                                            {formatCurrency(supplierDetails.accountBalance || 0, currency)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Active Purchase Orders</Typography>
                                        <Typography variant="body2">{supplierDetails.activePOCount || 0}</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Box>
                    ) : (
                        <Typography color="text.secondary">Supplier details not found.</Typography>
                    )}
                </Box>
            </Drawer>

            {/* Purchase Order Details Drawer */}
            <Drawer
                anchor="right"
                open={poDrawerOpen}
                onClose={() => setPoDrawerOpen(false)}
            >
                <Box sx={{ width: { xs: '100vw', sm: 500, md: 600 }, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>Purchase Order Details</Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    {poDetailsLoading ? (
                        <Box>
                            <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 2 }} />
                            <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 2 }} />
                            <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 2 }} />
                        </Box>
                    ) : poDetails ? (
                        <Box>
                            {/* Submit Order Action */}
                            {(poDetails.status === 'Draft' || poDetails.status === 'draft') && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <Button 
                                        variant="contained" 
                                        color="primary"
                                        onClick={handleSubmitPO}
                                        disabled={isSubmittingPO}
                                        startIcon={isSubmittingPO ? <CircularProgress size={20} color="inherit" /> : null}
                                    >
                                        {isSubmittingPO ? 'Submitting...' : 'Submit Order'}
                                    </Button>
                                </Box>
                            )}

                            {/* Review Actions */}
                            {(poDetails.status === 'Pending Approval' || poDetails.status === 'Submitted') && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
                                    <Button 
                                        variant="outlined" 
                                        color="error" 
                                        onClick={() => setPoRejectModalOpen(true)}
                                        disabled={isReviewingPO}
                                    >
                                        Reject
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="primary"
                                        onClick={() => setPoApproveModalOpen(true)}
                                        disabled={isReviewingPO}
                                    >
                                        Approve
                                    </Button>
                                </Box>
                            )}

                            {/* Receive Order Action */}
                            {(poDetails.status === 'Ordered' || poDetails.status === 'Approved') && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <Button 
                                        variant="contained" 
                                        color="primary"
                                        onClick={handleOpenReceivePO}
                                        disabled={isReceivingPO}
                                    >
                                        Log Shipment Intake
                                    </Button>
                                </Box>
                            )}

                            {/* Record Payment Action */}
                            {String(poDetails.status || '').toLowerCase() === 'delivered' && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <Button 
                                        variant="contained" 
                                        color="success"
                                        startIcon={<PaymentsIcon />}
                                        onClick={() => handleOpenPOPayment(poDetails)}
                                        disabled={isRecordingPOPayment}
                                        sx={{
                                            bgcolor: '#16a34a',
                                            '&:hover': { bgcolor: '#15803d' },
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            px: 2.5,
                                            boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)'
                                        }}
                                    >
                                        Record Payment
                                    </Button>
                                </Box>
                            )}

                            {/* PO Administrative Header */}
                            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Typography variant="subtitle2" color="text.secondary" mb={2}>ADMINISTRATIVE HEADER</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">PO Reference Number</Typography>
                                        <Typography variant="body2" fontWeight="bold">{poDetails.poNumber || poDetails.referenceCode || poDetails.id}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Order Status</Typography>
                                        <Box mt={0.5}>
                                            <Chip 
                                                label={poDetails.status || 'Draft'} 
                                                size="small" 
                                                color={getPOStatusColor(poDetails.status)} 
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Creation Date</Typography>
                                        <Typography variant="body2">{poDetails.createdAt || poDetails.issueDate ? new Date(poDetails.createdAt || poDetails.issueDate).toLocaleString() : 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Raised By</Typography>
                                        <Typography variant="body2">{poDetails.raisedBy?.name || 'System'}</Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Vendor & Delivery Context */}
                            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Typography variant="subtitle2" color="text.secondary" mb={2}>VENDOR & DELIVERY CONTEXT</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Supplier / Vendor</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {poDetails.supplierName || (poDetails.supplier ? poDetails.supplier.companyName || poDetails.supplier.name : 'Unknown Vendor')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Expected Delivery Date</Typography>
                                        <Typography variant="body2">
                                            {poDetails.expectedDeliveryDate ? new Date(poDetails.expectedDeliveryDate).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    {poDetails.notes && (
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Order Notes</Typography>
                                            <Typography variant="body2">{poDetails.notes}</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>

                            {/* Line-Items Table Array */}
                            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }} elevation={0}>
                                <Typography variant="subtitle2" color="text.secondary" mb={2}>LINE ITEMS</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item Name / SKU</TableCell>
                                                <TableCell align="right">Qty</TableCell>
                                                <TableCell align="right">Unit Cost</TableCell>
                                                <TableCell align="right">Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(!poDetails.items || poDetails.items.length === 0) ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">No line items found.</TableCell>
                                                </TableRow>
                                            ) : poDetails.items.map((item, idx) => (
                                                <TableRow key={item.id || idx}>
                                                    <TableCell>{item.itemName || (item.item ? item.item.name || item.item.sku : 'Unknown Item')}</TableCell>
                                                    <TableCell align="right">{item.quantityOrdered || item.quantity || 0}</TableCell>
                                                    <TableCell align="right">{formatCurrency(item.unitCost || item.price || 0, currency)}</TableCell>
                                                    <TableCell align="right"><b>{formatCurrency((item.quantityOrdered || item.quantity || 0) * (item.unitCost || item.price || 0), currency)}</b></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Financial Summary Block */}
                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }} elevation={0}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Total Order Value</Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {formatCurrency(poDetails.totalCost || poDetails.totalAmount || poDetails.totalValue || poDetails.orderValue || 0, currency)}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>
                    ) : (
                        <Typography color="text.secondary">Purchase order details not found.</Typography>
                    )}
                </Box>
            </Drawer>

            <PurchaseOrderModals
                createPOModalOpen={createPOModalOpen}
                setCreatePOModalOpen={setCreatePOModalOpen}
                isCreatingPO={isCreatingPO}
                poForm={poForm}
                setPoForm={setPoForm}
                suppliers={suppliers}
                inventoryItems={inventoryItems}
                handleRemovePOLineItem={handleRemovePOLineItem}
                handlePOLineItemChange={handlePOLineItemChange}
                handleAddPOLineItem={handleAddPOLineItem}
                handleCreatePO={handleCreatePO}
                poApproveModalOpen={poApproveModalOpen}
                setPoApproveModalOpen={setPoApproveModalOpen}
                isReviewingPO={isReviewingPO}
                handleApprovePO={handleApprovePO}
                poRejectModalOpen={poRejectModalOpen}
                setPoRejectModalOpen={setPoRejectModalOpen}
                poRejectReason={poRejectReason}
                setPoRejectReason={setPoRejectReason}
                handleRejectPO={handleRejectPO}
                poReceiveModalOpen={poReceiveModalOpen}
                setPoReceiveModalOpen={setPoReceiveModalOpen}
                isReceivingPO={isReceivingPO}
                poReceiveForm={poReceiveForm}
                setPoReceiveForm={setPoReceiveForm}
                handleReceivePO={handleReceivePO}
                poPaymentModalOpen={poPaymentModalOpen}
                setPoPaymentModalOpen={setPoPaymentModalOpen}
                isRecordingPOPayment={isRecordingPOPayment}
                poPaymentForm={poPaymentForm}
                setPoPaymentForm={setPoPaymentForm}
                handleRecordPOPayment={handleRecordPOPayment}
                selectedPOForPayment={selectedPOForPayment}
                paymentMethods={paymentMethods}
                loadingPaymentMethods={loadingPaymentMethods}
            />

        </Box>
    );
}
